import { useCallback, useEffect, useRef, useState } from "react";
import {
  makeComposite,
  coverSrcRect,
  drawCanvasCroppedToContainer,
} from "../utils/compositeUtils";
import { ensureMp4 } from "../utils/convertToMp4";

const LONG_PRESS_MS = 300;

export default function useCameraRecorder({
  webglCanvasRef,
  containerRef,
  reviewVideoRef,
  apiRef,
}) {
  const mediaRecorderRef = useRef(null);
  const rafRef = useRef(0);
  const micStreamRef = useRef(null);
  const pressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  const [recording, setRecording] = useState(false);
  const [arming, setArming] = useState(false);
  const [videoURL, setVideoURL] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [posterURL, setPosterURL] = useState("");
  const [showPlayOverlay, setShowPlayOverlay] = useState(false);
  const [transcoding, setTranscoding] = useState(false);
  const [shareMime, setShareMime] = useState("video/webm");
  const [isFront, setIsFront] = useState(true);

  const topImgRef = useRef(null);
  const botImgRef = useRef(null);
  const posterCapturedRef = useRef(false);

  const setMicStream = useCallback((stream) => {
    micStreamRef.current = stream;
  }, []);


  const findSourceVideo = useCallback(() => {
    return (
      apiRef.current?.getSourceVideo?.() ||
      document.querySelector(".video-container video") ||
      null
    );
  }, [apiRef]);

  const waitForVideoReady = useCallback(
    (v) =>
      new Promise((resolve) => {
        if (v?.readyState >= 2 && v.videoWidth > 0) return resolve();
        v?.addEventListener("canplay", () => resolve(), { once: true });
        setTimeout(resolve, 1500);
      }),
    []
  );

  // draw top+bottom PNG frames
  const drawFrames = (ctx, w, h) => {
    const top = topImgRef.current;
    const bot = botImgRef.current;

    if (top && top.naturalWidth) {
      const th = Math.round((top.naturalHeight / top.naturalWidth) * w);
      ctx.drawImage(top, 0, 0, w, th);
    }
    if (bot && bot.naturalWidth) {
      const bh = Math.round((bot.naturalHeight / bot.naturalWidth) * w);
      ctx.drawImage(bot, 0, h - bh, w, bh);
    }
  };

  // load frame images
  useEffect(() => {
    const top = new Image();
    const bot = new Image();
    top.onload = () => (topImgRef.current = top);
    bot.onload = () => (botImgRef.current = bot);
    top.src = "/assets/images/frame/frame1.png";
    bot.src = "/assets/images/frame/frame2.png";
  }, []);

  useEffect(() => {
    return () => {
      try {
        micStreamRef.current?.getTracks().forEach((t) => t.stop());
      } catch {}
    };
  }, []);

  // preview controls
  useEffect(() => {
    const v = reviewVideoRef.current;
    if (!v) return;

    const handleEnded = () => setShowPlayOverlay(true);
    v.addEventListener("ended", handleEnded);

    return () => v.removeEventListener("ended", handleEnded);
  }, [reviewVideoRef]);

  useEffect(() => () => videoURL && URL.revokeObjectURL(videoURL), [videoURL]);
  useEffect(() => () => photoURL && URL.revokeObjectURL(photoURL), [photoURL]);
  useEffect(() => () => posterURL && URL.revokeObjectURL(posterURL), [posterURL]);

  useEffect(() => {
    const v = reviewVideoRef.current;
    if (!v) return;

    if (videoURL) {
      v.pause();
      v.muted = false;
      v.loop = false;
      v.controls = false;
      v.currentTime = 0;
      setShowPlayOverlay(true);
    }

    (async () => {
      try {
        v.muted = false;
        v.autoplay = true;
        v.loop = true;
        v.controls = false;
        v.currentTime = 0;
        await v.play();
        setShowPlayOverlay(false);
      } catch {}
    })();
  }, [videoURL, reviewVideoRef]);

  const retake = useCallback(() => {
    try {
      const rv = reviewVideoRef.current;
      if (rv) {
        rv.pause();
        rv.removeAttribute("src");
        rv.load();
      }
    } catch {}

    if (videoURL) URL.revokeObjectURL(videoURL);
    if (photoURL) URL.revokeObjectURL(photoURL);

    setVideoURL("");
    setPhotoURL("");
    setShowPlayOverlay(false);

    setPosterURL((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });

    posterCapturedRef.current = false;
  }, [photoURL, reviewVideoRef, videoURL]);

  const takePhoto = useCallback(async () => {
    const webglCanvas = webglCanvasRef.current;
    const container = containerRef.current;
    const video = findSourceVideo();

    if (!webglCanvas || !container || !video) {
      return alert("Camera not ready.");
    }

    await waitForVideoReady(video);

    const compCtx = makeComposite(container);
    const { comp, ctx, w, h, dpr } = compCtx;

    const vRect = coverSrcRect(video.videoWidth, video.videoHeight, w, h);
    await new Promise(requestAnimationFrame);

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // mirror camera
    ctx.save();
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, vRect.sx, vRect.sy, vRect.sw, vRect.sh, 0, 0, w, h);
    ctx.restore();

    // AR overlay
    if (drawCanvasCroppedToContainer) {
      drawCanvasCroppedToContainer(ctx, { w, h, dpr }, webglCanvas, container);
    }

    // PNG frames
    drawFrames(ctx, w, h);

    await new Promise((resolve) =>
      comp.toBlob(
        (blob) => {
          if (!blob) return resolve();
          const url = URL.createObjectURL(blob);
          setPhotoURL((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
          });
          setVideoURL("");
          setShareMime("image/jpeg");
          resolve();
        },
        "image/jpeg",
        0.92
      )
    );
  }, [containerRef, findSourceVideo, waitForVideoReady, webglCanvasRef]);

  const startRecording = useCallback(async () => {
    const webglCanvas = webglCanvasRef.current;
    const container = containerRef.current;
    const video = findSourceVideo();

    if (!webglCanvas || !container || !video) return alert("Camera not ready.");

    await waitForVideoReady(video);

    posterCapturedRef.current = false;

    const compCtx = makeComposite(container);
    const { comp, ctx, w, h, dpr } = compCtx;

    const drawOnce = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // mirror camera
      const vRect = coverSrcRect(video.videoWidth || 1, video.videoHeight || 1, w, h);
      ctx.save();
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, vRect.sx, vRect.sy, vRect.sw, vRect.sh, 0, 0, w, h);
      ctx.restore();

      // AR overlay
      if (drawCanvasCroppedToContainer) {
        drawCanvasCroppedToContainer(ctx, { w, h, dpr }, webglCanvas, container);
      }

      // PNG frames
      drawFrames(ctx, w, h);
    };

    let stopped = false;

    const drawLoop = () => {
      if (stopped) return;
      drawOnce();

      if (!posterCapturedRef.current) {
        comp.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setPosterURL((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return url;
            });
            posterCapturedRef.current = true;

            const v = reviewVideoRef.current;
            if (v) v.poster = url;
          }
        }, "image/jpeg");
      }

      rafRef.current = requestAnimationFrame(drawLoop);
    };

    rafRef.current = requestAnimationFrame(drawLoop);

    const stream = comp.captureStream(30);

    if (micStreamRef.current) {
      micStreamRef.current.getAudioTracks().forEach((t) => stream.addTrack(t));
    }

    // pick codec
    const mimeType =
      [
        "video/mp4;codecs=h264,aac",
        "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
      ].find((t) => MediaRecorder.isTypeSupported(t)) || "";

    const mr = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 3_000_000,
    });

    mediaRecorderRef.current = mr;
    const chunks = [];

    mr.ondataavailable = (e) => e.data && chunks.push(e.data);

    mr.onstop = async () => {
      stopped = true;
      cancelAnimationFrame(rafRef.current);

      const rawBlob = new Blob(chunks, { type: mr.mimeType || "video/webm" });

      try {
        setTranscoding(true);
        const ensured = await ensureMp4(
          { blob: rawBlob, mimeType: rawBlob.type, filename: "recording" },
          { speedProfile: "veryfast", fps: 30 }
        );

        setVideoURL(ensured.url);
        setShareMime(ensured.mimeType);
      } catch (e) {
        const url = URL.createObjectURL(rawBlob);
        setVideoURL(url);
        setShareMime(rawBlob.type || "video/webm");
      } finally {
        setTranscoding(false);
      }

      setPhotoURL("");
      queueMicrotask(() => reviewVideoRef.current?.load());
    };

    mr.start();
    setRecording(true);
  }, [
    apiRef,
    containerRef,
    ensureMp4,
    findSourceVideo,
    reviewVideoRef,
    waitForVideoReady,
    webglCanvasRef,
  ]);

  const stopRecording = useCallback(() => {
    try {
      mediaRecorderRef.current?.stop();
    } catch {}
    setRecording(false);
    setArming(false);
  }, []);

  const onRecordPointerDown = useCallback(() => {
    longPressFiredRef.current = false;
    setArming(true);

    pressTimerRef.current = setTimeout(async () => {
      longPressFiredRef.current = true;
      try {
        await startRecording();
      } finally {
        setArming(false);
      }
    }, LONG_PRESS_MS);
  }, [startRecording]);

  const onRecordPointerUpOrCancel = useCallback(async () => {
    clearTimeout(pressTimerRef.current);

    if (recording) {
      stopRecording();
    } else if (!longPressFiredRef.current) {
      await takePhoto();
    }

    setArming(false);
  }, [recording, stopRecording, takePhoto]);

  const flipCamera = useCallback(async () => {
    const res = await apiRef.current?.flip();
    if (res?.ok) setIsFront(res.shouldFaceUser);
  }, [apiRef]);

  const playWithSound = useCallback(async () => {
    const v = reviewVideoRef.current;
    if (!v) return;
    try {
      v.muted = false;
      v.autoplay = true;
      v.loop = true;
      v.controls = false;
      v.currentTime = 0;
      await v.play();
      setShowPlayOverlay(false);
    } catch {}
  }, [reviewVideoRef]);

  return {
    recording,
    arming,
    videoURL,
    photoURL,
    posterURL,
    showPlayOverlay,
    transcoding,
    shareMime,
    isFront,

    onRecordPointerDown,
    onRecordPointerUpOrCancel,
    stopRecording,
    takePhoto,
    retake,
    flipCamera,
    playWithSound,

    setMicStream,
  };
}
